// Copyright (c) 2026 Anchorbase, Inc. All rights reserved.

import { z } from 'zod';

export const STANDALONE_PAYMENTS_BOOTSTRAP_PROVIDERS = ['valor', 'cardpointe', 'clover'] as const;

export type StandalonePaymentsBootstrapProvider =
    (typeof STANDALONE_PAYMENTS_BOOTSTRAP_PROVIDERS)[number];

const StandalonePaymentsMachineProviderConfigurationSchema = z
    .object({
        merchant_account_uuid: z.string().uuid(),
        processor_name: z.string().min(1),
        credential_uuid: z.string().uuid(),
    })
    .strict();

export const StandalonePaymentsBootstrapProviderConfigurationSchema = z.discriminatedUnion(
    'provider',
    [
        StandalonePaymentsMachineProviderConfigurationSchema.extend({
            provider: z.literal('valor'),
            ipg_system_id: z.literal(1),
            authorization_kind: z.literal('machine_credential'),
        }).strict(),
        StandalonePaymentsMachineProviderConfigurationSchema.extend({
            provider: z.literal('cardpointe'),
            ipg_system_id: z.literal(2),
            authorization_kind: z.literal('machine_credential'),
        }).strict(),
        StandalonePaymentsMachineProviderConfigurationSchema.extend({
            provider: z.literal('clover'),
            ipg_system_id: z.literal(3),
            authorization_kind: z.literal('oauth'),
        }).strict(),
    ]
);

export const StandalonePaymentsBootstrapStateSchema = z
    .object({
        state_version: z.literal('v1'),
        run_suffix: z.string().min(1),
        merchant: z
            .object({
                organization_uuid: z.string().uuid(),
                organization_name: z.string().min(1),
            })
            .strict(),
        user: z
            .object({
                user_uuid: z.string().uuid(),
                email: z.string().email(),
            })
            .strict(),
        role_assignment: z
            .object({
                role_assignment_uuid: z.string().uuid(),
                role: z.literal('REP'),
            })
            .strict(),
        provider_configuration: StandalonePaymentsBootstrapProviderConfigurationSchema,
        virtual_terminal: z
            .object({
                logical_terminal_uuid: z.string().uuid(),
                terminal_device_uuid: z.string().uuid(),
                terminal_name: z.string().min(1),
                processor_tid: z.string().min(1),
            })
            .strict(),
        authentication_identity: z
            .object({
                real_user_uuid: z.string().uuid(),
                effective_user_uuid: z.string().uuid(),
                real_role_assignment_uuid: z.string().uuid(),
                effective_role_assignment_uuid: z.string().uuid(),
                impersonating: z.literal(false),
            })
            .strict(),
        transaction_evidence: z
            .object({
                payment_transaction_uuid: z.string().uuid(),
                status: z.string().min(1),
                provider_attempt_count: z.literal(1),
                pan_persisted: z.literal(false),
                cvv_persisted: z.literal(false),
            })
            .strict()
            .nullable(),
    })
    .strict();

export type StandalonePaymentsBootstrapState = z.infer<
    typeof StandalonePaymentsBootstrapStateSchema
>;
